import React, { createContext, useCallback, useContext, useState } from 'react';
import './Toast.css';

/**
 * Lightweight in-house toast/snackbar system - no external dependency.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success('Quote saved!');
 *   toast.error('Failed to save quote.');
 *
 * Mounted once at the app root (see App.js) so any page can call useToast()
 * without needing its own provider.
 */
const ToastContext = createContext(null);

let toastIdCounter = 0;

const DEFAULT_DURATION = 4000;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message, options = {}) => {
    const { type = 'info', duration = DEFAULT_DURATION } = options;
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, message, type }]);
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
    return id;
  }, [removeToast]);

  const toast = {
    show: showToast,
    success: (message, options) => showToast(message, { ...options, type: 'success' }),
    error: (message, options) => showToast(message, { ...options, type: 'error' }),
    warning: (message, options) => showToast(message, { ...options, type: 'warning' }),
    info: (message, options) => showToast(message, { ...options, type: 'info' }),
    dismiss: removeToast
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="toast-container" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            <span className="toast-icon">
              {t.type === 'success' && '✓'}
              {t.type === 'error' && '✕'}
              {t.type === 'warning' && '⚠'}
              {t.type === 'info' && 'ℹ'}
            </span>
            <span className="toast-message">{t.message}</span>
            <button
              type="button"
              className="toast-close"
              aria-label="Dismiss"
              onClick={() => removeToast(t.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// Falls back to no-op functions instead of throwing when used outside a
// ToastProvider (e.g. in isolated tests), so adopting it in a page is never
// a breaking change if the provider isn't mounted yet.
const noop = () => {};
const fallbackToast = { show: noop, success: noop, error: noop, warning: noop, info: noop, dismiss: noop };

export const useToast = () => {
  const ctx = useContext(ToastContext);
  return ctx || fallbackToast;
};

export default ToastProvider;
